import { useState } from 'react'

import {
  ComboButton,
  Modal,
  ModalProvider,
  PrimaryButton,
  SecondaryButton,
  TabPanel,
  TabularMenu,
  TabularMenuProvider,
  useSteps,
} from '@joor/design-system'
import { useFlags } from '@joor/launchdarkly-react-client-sdk'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { OrderDiscountType } from '__generated__/atlas-types'

import { decimalWithCommas } from 'utils/formatters'

import usePortal from 'hooks/usePortal'
import { useRenderToasters } from 'hooks/useRenderToasters'
import { getCurrentUser } from 'store/currentUser/selectors'

import DetailsTab from './DetailsTab/DetailsTab'
import EmailPreviewTab from './EmailPreviewTab/EmailPreviewTab'
import { useEmailFields } from './sendPaymentRequest.hooks'
import {
  useCreateInvoice,
  useSendInvoice,
} from './sendPaymentRequest.mutations'
import { useOrderRemainingBalance } from 'features/Orders/PublicInvoice/components/payment.hooks'
import { sendPaymentRequestIds } from 'features/Orders/orders.ids'

const ModalButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
`

const StyledModal = styled(Modal)`
  overflow: visible;
  height: 80%;
  max-height: 750px;
`

export type SendPaymentRequestModalProps = {
  isOpen?: boolean
  setIsOpen?: (open: boolean) => void
  onSuccess?: (updatedIds: string[]) => void
  // consider making them required at the end
  orderId?: string
  orderPO?: string
  retailerId?: string
  retailerEmail?: string
  retailerName?: string
  webOrderId?: string
  brandId?: string
  brandName?: string
  orderCurrency?: string
  billingAddress?: string
  hasRefetchPaymentInfo?: boolean
}

export enum PaymentType {
  FULL = 'full',
  PARTIAL = 'partial',
}

const SendPaymentRequestModal = ({
  isOpen = false,
  setIsOpen = () => {},
  onSuccess,
  orderId,
  orderPO,
  orderCurrency,
  retailerId,
  retailerEmail,
  retailerName,
  webOrderId,
  brandId,
  brandName,
  billingAddress,
  hasRefetchPaymentInfo,
}: SendPaymentRequestModalProps) => {
  const Portal = usePortal()
  const baseUrl = window.location.origin
  const currentUser = useSelector(getCurrentUser)
  const [copiedLink, setCopiedLink] = useState<string>('')
  const [copyLinkLoading, setCopyLinkLoading] = useState(false)
  const [index, setIndex] = useState(0)
  const [paymentType, setPaymentType] = useState(PaymentType.FULL)
  const [sendEmailLoading, setSendEmailLoading] = useState(false)
  const [disableButtons, setDisableButtons] = useState(false)
  const [numericInputFilled, setNumericInputFilled] = useState('')
  const {
    emailFields,
    handleUploading,
    handleUpdateAttachmentLoading,
    handleDeleteAttachment,
    handleResetPayment,
    handleUpdateEmail,
    handleUpdateEmailBody,
    handleUpdateEmailSubject,
    handleUpdatePaymentAmount,
    handleUpdatePaymentName,
    handleUpdateDueDate,
  } = useEmailFields()
  const { currentStep, nextStep, previousStep } = useSteps(2)
  const { renderSuccessToast, renderErrorToast } = useRenderToasters()
  const { createInvoice } = useCreateInvoice(
    hasRefetchPaymentInfo ? orderId : undefined,
  )
  const { sendInvoice } = useSendInvoice(
    hasRefetchPaymentInfo ? orderId : undefined,
  )
  const { remainingBalance, loading: loadingRemainingBalance } =
    useOrderRemainingBalance(orderId)

  const { sendPaymentRequestDueDate } = useFlags()

  const checkAllSpaces = (str: string) => {
    return !/\S/.test(str)
  }

  const fieldHasOnlySpaces =
    checkAllSpaces(emailFields.subject) || checkAllSpaces(emailFields.body)

  const isAmountOnlyZeros = /^0*$/.test(numericInputFilled)
  const isAmountCombinationOfZerosWithDot = /^0*\.0*$/.test(numericInputFilled)

  const invalidNumericInput =
    (emailFields.paymentAmount.amount === '0' &&
      numericInputFilled.length === 0) ||
    isAmountCombinationOfZerosWithDot ||
    isAmountOnlyZeros

  const isDueDateEmpty = sendPaymentRequestDueDate && !emailFields.dueDate
  const checkEmptyPaymentFields =
    (paymentType === PaymentType.PARTIAL &&
      (!emailFields.paymentName ||
        !emailFields.paymentAmount.amount ||
        checkAllSpaces(emailFields.paymentName) ||
        invalidNumericInput)) ||
    isDueDateEmpty

  const calculatedPaymentAmount =
    emailFields.paymentAmount.format === OrderDiscountType.PERCENT
      ? `${
          (remainingBalance || 0) *
          (Number(emailFields.paymentAmount.amount) / 100)
        }`
      : emailFields.paymentAmount.amount

  const selectedAmount =
    paymentType === PaymentType.FULL
      ? remainingBalance
      : calculatedPaymentAmount

  const handlePreviousStep = () => {
    previousStep()
    handleUpdateEmailSubject('')
    handleUpdateEmailBody('')
    handleDeleteAttachment()
    handleUpdateAttachmentLoading(false)
    setIndex(0)
  }

  const copyLink = async () => {
    setCopyLinkLoading(true)
    const params = emailFields.paymentName
      ? {
          orderId,
          retailerId,
          isPartialPaymentRequest: true,
          amount: decimalWithCommas(calculatedPaymentAmount),
          name: emailFields.paymentName,
          currency: orderCurrency,
          dueDate: emailFields.dueDate,
        }
      : {
          orderId,
          retailerId,
          amount: decimalWithCommas(remainingBalance),
          currency: orderCurrency,
          dueDate: emailFields.dueDate,
        }
    const result = await createInvoice(params)
    if (result.errors) {
      renderErrorToast({
        title: 'Unable to copy link',
        description: 'An unexpected server error has occurred. Try again.',
        shouldCloseManually: true,
      })
    } else {
      await navigator.clipboard.writeText(
        `${baseUrl}/invoices/pub/${result?.data?.createInvoices?.[0].invoiceId}`,
      )
      setCopiedLink(orderId!)
      setCopyLinkLoading(false)
    }
  }

  const sendInvoiceEmail = async () => {
    setSendEmailLoading(true)
    const result = await sendInvoice({
      orderId,
      webOrderId,
      retailerId,
      brandId,
      poNumber: orderPO,
      bccEmails: emailFields.emails.map((email) => email.value),
      sentToEmail: retailerEmail,
      billingAddress,
      retailerName,
      retailerEmail,
      customSubject: emailFields.subject,
      customMessage: emailFields.body,
      attachments: emailFields.attachment ? [emailFields.attachment] : [],
      brandContactName: currentUser?.name || currentUser?.email || '--',
      isPartialPaymentRequest: !!emailFields.paymentName,
      currency: orderCurrency,
      amount: decimalWithCommas(selectedAmount),
      name: emailFields.paymentName,
      dueDate: emailFields.dueDate,
    })
    if (result?.errors) {
      renderErrorToast({
        title: 'Unable to Send Emails',
        description:
          'An error has occurred. Please check if an email is associated with the selected order(s).',
        shouldCloseManually: true,
      })
    } else {
      renderSuccessToast({
        title: 'Email(s) successful',
        description: 'Email(s) have been successfully sent.',
        shouldCloseManually: true,
      })
      const updatedId = result.data?.sendInvoicesForOrders?.[0]?.orderId
      if (updatedId && onSuccess) {
        onSuccess([updatedId])
      }
    }
    setSendEmailLoading(false)
    setIsOpen(false)
  }

  const tabs = [
    {
      id: sendPaymentRequestIds.detailsTab,
      component: (
        <DetailsTab
          orderInfo={{
            id: orderId,
            po: orderPO,
            currency: orderCurrency,
            brandId,
            retailerId,
            retailerEmail,
            retailerName,
            remainingAmount: `${remainingBalance}`,
          }}
          emails={emailFields.emails}
          setEmails={handleUpdateEmail}
          emailSubject={emailFields.subject}
          setEmailSubject={handleUpdateEmailSubject}
          emailBody={emailFields.body}
          setEmailBody={handleUpdateEmailBody}
          attachment={emailFields.attachment}
          attachmentLoading={emailFields.attachmentLoading}
          handleUploading={handleUploading}
          handleUpdateAttachmentLoading={handleUpdateAttachmentLoading}
          paymentName={emailFields.paymentName}
          setPaymentName={handleUpdatePaymentName}
          paymentAmount={emailFields.paymentAmount}
          setPaymentAmount={handleUpdatePaymentAmount}
          dueDate={emailFields.dueDate}
          setDueDate={handleUpdateDueDate}
          paymentType={paymentType}
          setPaymentType={setPaymentType}
          onDeleteAttachment={handleDeleteAttachment}
          onFieldError={setDisableButtons}
          currentStep={currentStep}
          loading={!!loadingRemainingBalance}
          onResetPayment={handleResetPayment}
          onNumericInputFilled={setNumericInputFilled}
          selectedAmount={decimalWithCommas(selectedAmount)}
        />
      ),
      label: 'Details',
      onClick: () => setIndex(0),
    },
    {
      id: sendPaymentRequestIds.emailPreview,
      component: (
        <EmailPreviewTab
          brandName={brandName}
          subject={emailFields.subject}
          billingAddress={billingAddress}
          retailerName={retailerName}
          retailerEmail={retailerEmail}
          body={emailFields.body}
          attachment={emailFields.attachment}
          paymentName={emailFields.paymentName}
          paymentAmount={`${decimalWithCommas(
            selectedAmount,
          )} ${orderCurrency}`}
          dueDate={emailFields.dueDate}
        />
      ),
      label: 'Email Preview',
      onClick: () => setIndex(1),
    },
  ]

  // we have to do this check because of a rounding error :(
  const isRemainingBalanceZeroOrLess = (remainingBalance || 0) <= 0

  const orderPaid =
    currentStep === 0 &&
    isRemainingBalanceZeroOrLess &&
    !loadingRemainingBalance

  const orderHasRemainingBalance =
    currentStep === 0 && remainingBalance > 0 && !loadingRemainingBalance

  const getTabs = () => {
    return orderPaid ? [tabs[0]] : tabs
  }

  const handleCustomizedRequestClick = () => {
    return index === 1 ? setIndex(0) : nextStep()
  }

  return (
    <Portal>
      <ModalProvider>
        <StyledModal
          title={{ text: 'Send Payment Request', iconName: 'plane' }}
          subtitle={
            orderHasRemainingBalance
              ? `The request email will be sent from a JOOR email address.
          If you would prefer to send from your own email, you can copy the link
          and paste it into any email to access the payments page.`
              : ''
          }
          isOpen={isOpen}
          onClose={(): void => {
            if (!!copiedLink) {
              onSuccess && onSuccess([copiedLink])
            }
            setIsOpen(false)
          }}
          disableBackgroundClick
        >
          <Modal.Content>
            <TabularMenuProvider index={index} tabs={getTabs()}>
              <TabularMenu />
              <TabPanel />
            </TabularMenuProvider>
          </Modal.Content>
          <Modal.Actions>
            <ModalButtonsContainer>
              {orderPaid && (
                <PrimaryButton
                  id={sendPaymentRequestIds.closeButton}
                  onClick={() => setIsOpen(false)}
                >
                  Close
                </PrimaryButton>
              )}
              {orderHasRemainingBalance && (
                <>
                  <SecondaryButton
                    id={sendPaymentRequestIds.copyLinkButton}
                    onClick={copyLink}
                    onBlur={() => setCopiedLink('')}
                    loading={copyLinkLoading}
                    disabled={disableButtons || checkEmptyPaymentFields}
                  >
                    {copiedLink ? 'Link Copied' : 'Copy Link'}
                  </SecondaryButton>
                  <ComboButton
                    id={sendPaymentRequestIds.comboButtonAutomated}
                    onClick={sendInvoiceEmail}
                    options={[
                      {
                        label: 'Send Customized Request',
                        onClick: handleCustomizedRequestClick,
                        id: sendPaymentRequestIds.comboButtonCustomized,
                      },
                    ]}
                    disabled={disableButtons || checkEmptyPaymentFields}
                  >
                    Send Automated Request
                  </ComboButton>
                </>
              )}
              {currentStep === 1 && (
                <>
                  <SecondaryButton
                    id={sendPaymentRequestIds.goBackButton}
                    onClick={handlePreviousStep}
                  >
                    Go Back
                  </SecondaryButton>
                  <PrimaryButton
                    id={sendPaymentRequestIds.sendCustomizedRequestButton}
                    onClick={sendInvoiceEmail}
                    loading={sendEmailLoading}
                    disabled={
                      !emailFields.subject ||
                      !emailFields.body ||
                      emailFields.attachmentLoading ||
                      fieldHasOnlySpaces
                    }
                  >
                    Send Customized Request
                  </PrimaryButton>
                </>
              )}
            </ModalButtonsContainer>
          </Modal.Actions>
        </StyledModal>
      </ModalProvider>
    </Portal>
  )
}

export default SendPaymentRequestModal
